import gsap from "gsap";
import { CountUp } from "countup.js";
import $ from "jquery";

/**
 * Convertit les anciens easings GSAP2 ("Expo.easeOut", "Power2.easeInOut", etc.)
 * vers le format GSAP3 ("expo.out", "power2.inOut", etc.).
 * Si l'ease est déjà en format GSAP3, on le renvoie tel quel.
 */
function normalizeEase(ease) {
    if (!ease || typeof ease !== "string") return "expo.out";

    // Déjà en GSAP3 (ex: "expo.out", "power2.inOut")
    if (ease.includes(".") && !ease.includes("ease")) return ease;

    // Format GSAP2 le plus courant: "Expo.easeOut"
    const parts = ease.split(".");
    const familyRaw = (parts[0] || "").trim(); // "Expo", "Power2", etc.
    const typeRaw = (parts[1] || "").trim();   // "easeOut", "easeInOut", etc.

    const family = familyRaw.toLowerCase(); // "expo", "power2"
    const type = typeRaw
        .replace(/^ease/i, "") // "Out" / "InOut"
        .toLowerCase();        // "out" / "inout"

    // GSAP3 attend: out / in / inOut
    if (type === "inout") return `${family}.inOut`;
    if (type === "out") return `${family}.out`;
    if (type === "in") return `${family}.in`;

    // Fallback
    return "expo.out";
}

class ScrollInView {
    constructor(elements) {
        this.initScrollInView(elements);
    }

    initScrollInView(elements) {
        this.windowHeight = window.innerHeight;

        // Minimum distance required between top of the element and the bottom of the page to trigger an enter animation
        this.minVisibleHeight = 30;

        // Html attr used to detect elements which are grouped
        this.groupAttr = "data-scroll-in-view-group";

        // Html attr used to store the delay to start the element animation
        this.delayAttr = "data-scroll-in-view-delay";

        // Convert nodeList to array
        this.scrollingElements = Array.from(elements || []);

        if (this.scrollingElements.length) {
            this.scrollInViewElementsArray = [];
            this.remainingElementsToAnimCount = this.scrollingElements.length;

            for (let i = 0; i < this.scrollingElements.length; i++) {
                const el = this.scrollingElements[i];

                let attr = {
                    delay: 0,
                    duration: 1,
                    ease: "expo.out",
                    group: null,
                    trigger: false,
                };

                if (el.hasAttribute("data-scroll")) {
                    const raw = el.getAttribute("data-scroll");
                    try {
                        attr = { ...attr, ...JSON.parse(raw) };
                    } catch (e) {
                        console.error("Error while parsing json: ", raw, e, el);
                    }
                }

                const rect = el.getBoundingClientRect();
                const top = rect.top + window.pageYOffset;
                const height = rect.height;

                this.scrollInViewElementsArray[i] = {
                    index: i,
                    el,
                    $el: $(el),
                    height: Number.isFinite(height) ? height : parseInt(height, 10) || 0,
                    top: Number.isFinite(top) ? top : parseInt(top, 10) || 0,
                    delay: Number.isFinite(+attr.delay) ? parseFloat(attr.delay) : 0,
                    duration: Number.isFinite(+attr.duration) ? parseFloat(attr.duration) : 1,
                    ease: normalizeEase(attr.ease || "expo.out"),
                    group: Number.isFinite(+attr.group) ? parseInt(attr.group, 10) : null,
                    trigger: attr.trigger ? attr.trigger : false,
                    hasStarted: false,
                };
            }
        } else {
            this.scrollInViewElementsArray = [];
            this.remainingElementsToAnimCount = 0;
        }

        this.resize();
        this.bindEvents();
        this.scroll();
    }

    bindEvents() {
        this.scrollFn = this.scroll.bind(this);
        this.resizeFn = this.resize.bind(this);

        window.addEventListener("scroll", this.scrollFn, { passive: true });
        window.addEventListener("resize", this.resizeFn);
        window.addEventListener("load", this.resizeFn);
    }

    unbindEvents() {
        window.removeEventListener("scroll", this.scrollFn);
        window.removeEventListener("resize", this.resizeFn);
        window.removeEventListener("load", this.resizeFn);
        this.scrollFn = null;
        this.resizeFn = null;
    }

    scroll() {
        if (this.remainingElementsToAnimCount === 0) return;

        for (let i = 0; i < this.scrollInViewElementsArray.length; i++) {
            const element = this.scrollInViewElementsArray[i];
            if (!element) continue;

            if (this.canStartScrollInViewAnimation(element)) {
                if (!element.hasStarted) {
                    element.hasStarted = true;

                    this.animateScrollInViewElement(element);

                    // Groupe: déclenche tous les éléments du même groupe
                    if (element.group) {
                        for (let j = 0; j < this.scrollInViewElementsArray.length; j++) {
                            const groupEl = this.scrollInViewElementsArray[j];
                            if (!groupEl) continue;

                            if (groupEl.group && parseInt(groupEl.group, 10) === element.group) {
                                if (!groupEl.hasStarted) {
                                    groupEl.hasStarted = true;
                                    this.animateScrollInViewElement(groupEl);
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    isInScreen(element) {
        const scrollOffset = window.pageYOffset;

        // Le haut de l'élément est hors écran
        if (scrollOffset > element.top) {
            // Elément complètement hors écran (au-dessus)
            if (scrollOffset > element.top + element.height) return false;
            // Elément partiellement visible en haut de l'écran.
            return true;
        }

        // L'élément est visible au bas de l'écran.
        if (this.windowHeight + scrollOffset > element.top) return true;

        // Element pas encore visible.
        return false;
    }

    canStartScrollInViewAnimation(element) {
        return (
            this.isInScreen(element) &&
            this.windowHeight + window.pageYOffset - element.top >= this.minVisibleHeight
        );
    }

    animateScrollInViewElement(element) {
        const isCountUp = element.$el.hasClass("countup");
        const opacity = 1;

        // GSAP 3: gsap.to(target, { duration, ...vars })
        gsap.to(element.el, {
            duration: element.duration,
            x: 0,
            y: 0,
            rotation: 0,
            rotationX: 0,
            rotationY: 0,
            scaleX: 1,
            scaleY: 1,
            opacity,
            ease: element.ease,
            force3D: true,
            delay: element.delay,
            onComplete: () => {
                if (element.trigger) {
                    $(window).trigger(element.trigger, element);
                }

                element.$el.addClass("scroll-in-view-anim-ended");
                this.removeElementFromScrollInViewElementsArray(element);
            },
        });

        if (isCountUp) {
            const endVal = parseInt(element.$el.text(), 10);

            const options = {
                duration: 3,
                decimals: 0,
                separator: "",
                useGrouping: false,
            };

            const counter = new CountUp(element.el, endVal, options);

            if (!counter.error) counter.start();
            else console.log(counter.error);
        }
    }

    removeElementFromScrollInViewElementsArray(element) {
        if (this.scrollInViewElementsArray[element.index]) {
            this.scrollInViewElementsArray[element.index] = null;
            this.remainingElementsToAnimCount--;
        }
    }

    resize() {
        this.windowHeight = window.innerHeight;

        // Optionnel mais souvent utile: recalculer top/height au resize
        // (si ton layout change beaucoup, sinon tu peux retirer)
        for (let i = 0; i < this.scrollInViewElementsArray.length; i++) {
            const item = this.scrollInViewElementsArray[i];
            if (!item) continue;

            const rect = item.el.getBoundingClientRect();
            item.top = rect.top + window.pageYOffset;
            item.height = rect.height;
        }
    }

    destroy() {
        this.unbindEvents();
        this.scrollingElements.length = 0;
        this.scrollInViewElementsArray.length = 0;
    }
}

export default ScrollInView;
